<template>
  <Dialog :open="isOpenRef" @update:open="onOpenChange">
    <DialogContent
      class="z-[400] sm:max-w-md"
      :show-close-button="false"
      @pointer-down-outside="(e) => e.preventDefault()"
      @escape-key-down="(e) => e.preventDefault()"
      @interact-outside="(e) => e.preventDefault()"
    >
      <DialogHeader>
        <DialogTitle>{{ t('terms.modal.title') }}</DialogTitle>
        <DialogDescription>
          {{ t('terms.modal.bodyInformation') }}
        </DialogDescription>
      </DialogHeader>

      <p
        v-if="isReacceptanceRef"
        data-test-id="terms-modal-reacceptance-notice"
        class="bg-muted text-foreground rounded-md p-3 text-sm"
      >
        {{ t('terms.modal.updated') }}
      </p>

      <div class="flex items-start gap-3">
        <Checkbox
          id="terms-modal-cgu-checkbox"
          v-model="cguAcceptedRef"
          data-test-id="terms-modal-cgu-checkbox"
          class="mt-0.5"
        />
        <Label for="terms-modal-cgu-checkbox" class="leading-snug">
          <i18n-t keypath="invitation.cgu.label" tag="span">
            <template #cgu>
              <a href="/cgu" target="_blank" rel="noopener noreferrer" class="!text-primary !no-underline">
                {{ t('invitation.cgu.cguLink') }}
              </a>
            </template>
            <template #privacy>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" class="!text-primary !no-underline">
                {{ t('invitation.cgu.privacyLink') }}
              </a>
            </template>
          </i18n-t>
        </Label>
      </div>

      <DialogFooter>
        <Button
          type="button"
          class="w-full"
          :disabled="!cguAcceptedRef || isAcceptTermsPendingRef"
          data-test-id="terms-modal-accept-button"
          @click="onAccept"
        >
          {{ t('terms.modal.accept') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { acceptTermsMutation } from '@/features/legal/api'
import { selectGetTermsModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { invalidateAccount } from '@/utils/lib/vue-query'

const { t } = useI18n()
const { accountRef, isPendingRef } = useAccount()

const termsModalRef = selectGetTermsModal(modalsStore)
const isOpenRef = computed(() => !!termsModalRef.value?.isOpen)
const cguAcceptedRef = ref(false)
const isReacceptanceRef = computed(() => !!accountRef.value?.termsReacceptance)

watch(
  [accountRef, isPendingRef],
  () => {
    const hasAcceptedTerms = accountRef.value?.hasAcceptedTerms
    if (!isPendingRef.value && !hasAcceptedTerms) {
      modalsStore.toggleModal('terms', true)
    } else {
      modalsStore.toggleModal('terms', false)
    }
  },
  { immediate: true },
)

function onOpenChange(open: boolean) {
  if (!open) return
  modalsStore.toggleModal('terms', open)
}

const { mutate: acceptTerms, isPending: isAcceptTermsPendingRef } = useMutation({
  mutationFn: acceptTermsMutation,
  onError: () => {
    toast.error(t('error.common'))
  },
  onSuccess: async () => {
    await invalidateAccount()
    toast.success(t('terms.modal.success'))
    modalsStore.toggleModal('terms', false)
    cguAcceptedRef.value = false
  },
})

function onAccept() {
  acceptTerms()
}
</script>
